/**
 * Google Ads and Analytics utility functions
 * Handles conversion tracking and event reporting for the Super Week camp website
 */

export class AnalyticsService {
  // Enable debug mode for testing
  private static debugMode = process.env.NODE_ENV === 'development';

  /**
   * Track a conversion event for successful registration
   * This function calls the Google Ads conversion tracking
   */  static trackRegistrationConversion(): boolean {
    try {
      // Debug logging
      if (this.debugMode) {
        console.log('🎯 Attempting to track Google Ads conversion...');
        console.log('gtag_report_conversion available:', typeof window.gtag_report_conversion);
      }

      // Check if gtag_report_conversion function exists
      if (typeof window !== 'undefined' && window.gtag_report_conversion) {
        const result = window.gtag_report_conversion();
        console.log('✅ Google Ads registration conversion tracked successfully');
        
        if (this.debugMode) {
          console.log('🔍 Conversion tracking result:', result);
        }
        
        return result;
      } else {
        console.warn('⚠️ Google Ads conversion tracking not available - gtag_report_conversion function not found');
        
        if (this.debugMode) {
          console.log('🔍 Available window properties:', Object.keys(window).filter(key => key.includes('gtag')));
        }
        
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to track Google Ads conversion:', error);
      return false;
    }
  }

  /**
   * Track custom events with Google Analytics
   * @param eventName - Name of the event
   * @param parameters - Additional event parameters
   */  static trackEvent(eventName: string, parameters: Record<string, any> = {}): void {
    try {
      if (this.debugMode) {
        console.log(`🎯 Tracking event: ${eventName}`, parameters);
      }

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, {
          ...parameters,
          send_to: 'AW-17250955012'
        });
        console.log(`✅ Event '${eventName}' tracked successfully`, parameters);
      } else {
        console.warn('⚠️ Google Analytics not available - gtag function not found');
        
        if (this.debugMode) {
          console.log('🔍 Window gtag status:', typeof window?.gtag);
        }
      }
    } catch (error) {
      console.error(`❌ Failed to track event '${eventName}':`, error);
    }
  }

  /**
   * Track page views
   * @param pagePath - The page path to track
   */
  static trackPageView(pagePath: string): void {
    this.trackEvent('page_view', {
      page_path: pagePath
    });
  }

  /**
   * Track form interactions
   * @param formName - Name of the form
   * @param action - Action performed (start, complete, error)
   */
  static trackFormInteraction(formName: string, action: 'start' | 'complete' | 'error'): void {
    this.trackEvent('form_interaction', {
      form_name: formName,
      form_action: action
    });
  }
}
